// In src/pages/GroupsList.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, onSnapshot, doc, updateDoc, arrayUnion, arrayRemove, setDoc, serverTimestamp } from 'firebase/firestore';

import { Container, Typography, Button, CircularProgress, Box, Card, CardContent, CardActions, Grid, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

function GroupsList() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // REMOVED: const isLeadership = userProfile?.role === 'Leadership' || userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';

  // Define a specific check for who can create groups
  const canCreateGroups = userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';

  useEffect(() => {
    if (!userProfile?.churchId) {
      setLoading(false);
      return;
    }

    const groupsColRef = collection(db, 'churches', userProfile.churchId, 'groups');
    const unsubscribe = onSnapshot(groupsColRef, (snapshot) => {
      const groupsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setGroups(groupsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userProfile]);

  const handleJoinGroup = async (groupId) => {
    const groupDocRef = doc(db, 'churches', userProfile.churchId, 'groups', groupId);
    await updateDoc(groupDocRef, {
      memberUids: arrayUnion(currentUser.uid)
    });
  };
  
  const handleLeaveGroup = async (groupId) => {
    if (window.confirm("Are you sure you want to leave this group?")) {
      const groupDocRef = doc(db, 'churches', userProfile.churchId, 'groups', groupId);
      await updateDoc(groupDocRef, {
        memberUids: arrayRemove(currentUser.uid)
      });
    }
  };
  
  const handleRequestToJoin = async (groupId) => {
    const requestRef = doc(db, 'churches', userProfile.churchId, 'groups', groupId, 'joinRequests', currentUser.uid);
    try {
      await setDoc(requestRef, {
        userName: `${userProfile.firstName} ${userProfile.lastName}`,
        userId: currentUser.uid,
        requestedAt: serverTimestamp(),
      });
      alert('Your request to join has been sent to the group leader!');
    } catch (error) {
      console.error("Error sending join request:", error);
      alert("Failed to send join request.");
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Small Groups & Ministries</Typography>
        {canCreateGroups && ( // MODIFIED: Use canCreateGroups here
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/groups/new')}>
            Create Group
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {groups.map(group => {
          const isMember = group.memberUids?.includes(currentUser.uid);
          return (
            <Grid item xs={12} sm={6} md={4} key={group.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                    <Typography gutterBottom variant="h5" component="h2">{group.name}</Typography>
                    {!group.isPublic && <Chip label="Private" size="small" />}
                  </Box>
                  <Typography>{group.description}</Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => navigate(`/groups/${group.id}`)}>View</Button>
                  
                  {isMember ? (
                    <Button size="small" color="secondary" onClick={() => handleLeaveGroup(group.id)}>Leave</Button>
                  ) : (
                    group.isPublic ? (
                      <Button size="small" onClick={() => handleJoinGroup(group.id)}>Join</Button>
                    ) : (
                      <Button size="small" onClick={() => handleRequestToJoin(group.id)}>Request to Join</Button>
                    )
                  )}
                </CardActions>
              </Card>
            </Grid>
          )
        })}
         {groups.length === 0 && (
            <Grid item xs={12}>
                <Typography sx={{mt: 4, textAlign: 'center'}}>No groups have been created for this church yet.</Typography>
            </Grid>
        )}
      </Grid>
    </Container>
  );
}

export default GroupsList;